import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  DeleteOutlined,
  DownloadOutlined,
  LinkOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import type {
  WorkflowAttachment,
  WorkflowFieldDefinition,
  WorkflowReferenceValue,
  WorkflowRequest,
} from './model';
import type { WorkflowAttachmentPort, WorkflowRequestPort } from './port';
import { AnchoredMenu, AttachmentField, designTokens, FormAutocomplete, type FieldControlState } from '../../design-system';
import { WorkflowFileChip } from './WorkflowFileChip';
import { mergeWorkflowAttachmentRefresh, workflowAttachmentFingerprint } from './models/workflowAttachmentRefresh';
import { formatWorkflowDate } from './models/workflowRequestPageModel';

const acceptedFileExtensions = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tif,.tiff,.txt,.csv,.md,.json,.xml,.eml,.rtf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.hwp,.hwpx,.odt,.ods,.odp,.zip';

function attachmentsFromValue(value: unknown): WorkflowAttachment[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is WorkflowAttachment => Boolean(
    item && typeof item === 'object'
    && typeof (item as WorkflowAttachment).id === 'string'
    && typeof (item as WorkflowAttachment).displayName === 'string',
  ));
}

function referencesFromValue(value: unknown): WorkflowReferenceValue[] {
  const candidates = Array.isArray(value) ? value : value && typeof value === 'object' ? [value] : [];
  return candidates.filter((candidate): candidate is WorkflowReferenceValue => Boolean(
    candidate && typeof candidate === 'object'
    && typeof (candidate as WorkflowReferenceValue).id === 'string'
    && typeof (candidate as WorkflowReferenceValue).title === 'string',
  ));
}

function attachmentLimit(field: WorkflowFieldDefinition) {
  const configured = field.config?.maxFiles;
  return typeof configured === 'number' && Number.isInteger(configured) && configured >= 1 && configured <= 20
    ? configured
    : 10;
}

function referenceValue(request: WorkflowRequest): WorkflowReferenceValue {
  return {
    id: request.id,
    title: request.title,
    templateName: request.templateName,
    status: request.status,
    requesterName: request.requesterName,
    createdAt: request.createdAt,
  };
}

function saveBlob(blob: Blob, filename: string, openInline: boolean) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  if (openInline) {
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
  } else {
    anchor.download = filename;
  }
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export type WorkflowFileFieldControlProps = {
  scopeKey: string;
  field: WorkflowFieldDefinition;
  value: unknown;
  disabled: boolean;
  state: FieldControlState;
  port: WorkflowAttachmentPort;
  fixtureMode: boolean;
  ensureDraft: () => Promise<string>;
  onChange: (value: WorkflowAttachment[]) => void;
  onBusyChange?: (fieldKey: string, busy: boolean) => void;
};

/** 초안에 먼저 파일 객체를 저장한 뒤, 암호화된 workflow value에는 안전한 첨부 메타데이터만 유지합니다. */
export function WorkflowFileFieldControl(props: WorkflowFileFieldControlProps) {
  return <WorkflowFileFieldControlSession key={JSON.stringify([props.scopeKey, props.field.id, props.field.key])} {...props} />;
}

function WorkflowFileFieldControlSession({
  field,
  value,
  disabled,
  state,
  port,
  fixtureMode,
  ensureDraft,
  onChange,
  onBusyChange,
}: WorkflowFileFieldControlProps) {
  const [checkedAttachments, setCheckedAttachments] = useState<Record<string, { source: string; attachment: WorkflowAttachment }>>({});
  const sourceAttachments = useMemo(() => attachmentsFromValue(value), [value]);
  const attachments = useMemo(() => sourceAttachments.map((attachment) => {
    const checked = checkedAttachments[attachment.id];
    return checked?.source === workflowAttachmentFingerprint(attachment) ? checked.attachment : attachment;
  }), [sourceAttachments, checkedAttachments]);
  const latest = useRef({ attachments, sourceAttachments, onChange, disabled });
  latest.current = { attachments, sourceAttachments, onChange, disabled };
  const mounted = useRef(false);
  const busyRef = useRef(false);
  const checkingRef = useRef(false);
  const [selected, setSelected] = useState<WorkflowAttachment>();
  const [anchor, setAnchor] = useState<HTMLElement>();
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [localError, setLocalError] = useState('');
  const maximum = attachmentLimit(field);
  const remaining = maximum - attachments.length;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { onBusyChange?.(field.key, busy); }, [busy, field.key, onBusyChange]);
  useEffect(() => () => { onBusyChange?.(field.key, false); }, [field.key, onBusyChange]);
  const unresolvedAttachments = attachments.filter((attachment) => attachment.scanStatus !== 'CLEAN');
  const publishAttachments = (next: WorkflowAttachment[]) => {
    latest.current.attachments = next;
    latest.current.onChange(next);
  };
  const applyRefreshedAttachments = (requested: WorkflowAttachment[], refreshed: WorkflowAttachment[]) => {
    const current = latest.current.attachments;
    const next = mergeWorkflowAttachmentRefresh(current, requested, refreshed);
    const changes = next.filter((attachment, index) => attachment !== current[index]);
    if (changes.length === 0) return;
    const checkedChanges: typeof checkedAttachments = {};
    for (const attachment of changes) {
      const source = latest.current.sourceAttachments.find((item) => item.id === attachment.id);
      if (source) checkedChanges[attachment.id] = { source: workflowAttachmentFingerprint(source), attachment };
    }
    setCheckedAttachments((checked) => ({ ...checked, ...checkedChanges }));
    // 제출 결과를 확인하는 중에는 저장 스냅샷을 유지하고 조회 결과만 표시합니다.
    latest.current.attachments = next;
    if (!latest.current.disabled) latest.current.onChange(next);
  };
  const refreshScanStatuses = async () => {
    if (checkingRef.current || busyRef.current || fixtureMode) return;
    const requested = latest.current.attachments;
    checkingRef.current = true;
    setChecking(true); setLocalError('');
    try {
      const requestID = await ensureDraft();
      if (!mounted.current) return;
      const results = await Promise.allSettled(requested.map((attachment) => port.getWorkflowRequestAttachment(requestID, field.key, attachment.id)));
      if (!mounted.current) return;
      applyRefreshedAttachments(requested, results.flatMap((result) => result.status === 'fulfilled' ? [result.value] : []));
      const failure = results.find((result, index) => result.status === 'rejected'
        && latest.current.attachments.some((attachment) => attachment.id === requested[index].id));
      if (failure?.status === 'rejected') throw failure.reason;
    } catch (reason) {
      if (mounted.current) setLocalError(reason instanceof Error ? reason.message : '파일 상태를 확인하지 못했습니다. 다시 시도해 주세요.');
    } finally { if (mounted.current) { checkingRef.current = false; setChecking(false); } }
  };

  const uploadFiles = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (latest.current.disabled || busyRef.current || files.length === 0) return;
    if (files.length > maximum - latest.current.attachments.length) {
      setLocalError(`이 필드에는 파일을 최대 ${maximum}개까지 등록할 수 있습니다.`);
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setLocalError('');
    try {
      const requestID = await ensureDraft();
      if (!mounted.current) return;
      for (const file of files) {
        const attachment = fixtureMode
          ? {
              id: crypto.randomUUID().replaceAll('-', ''),
              displayName: file.name,
              contentType: file.type || 'application/octet-stream',
              sizeBytes: file.size,
              scanStatus: 'CLEAN',
              createdAt: new Date().toISOString(),
            }
          : await port.uploadWorkflowRequestAttachment(requestID, field.key, file);
        if (!mounted.current) return;
        publishAttachments([...latest.current.attachments, attachment]);
      }
    } catch (reason) {
      if (mounted.current) setLocalError(reason instanceof Error ? reason.message : '파일을 업로드하지 못했습니다.');
    } finally {
      if (mounted.current) { busyRef.current = false; setBusy(false); }
    }
  };

  const download = async (attachment: WorkflowAttachment, inline: boolean) => {
    setLocalError('');
    try {
      if (fixtureMode) return;
      const requestID = await ensureDraft();
      if (!mounted.current) return;
      const blob = await port.downloadWorkflowRequestAttachment(requestID, field.key, attachment.id, inline);
      if (!mounted.current) return;
      saveBlob(blob, attachment.displayName, inline);
    } catch (reason) {
      if (mounted.current) setLocalError(reason instanceof Error ? reason.message : inline ? '파일을 열지 못했습니다.' : '파일을 다운로드하지 못했습니다.');
    } finally {
      if (mounted.current) { setAnchor(undefined); setSelected(undefined); }
    }
  };

  const remove = async (attachment: WorkflowAttachment) => {
    if (latest.current.disabled || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setLocalError('');
    try {
      if (!fixtureMode) {
        const requestID = await ensureDraft();
        if (!mounted.current) return;
        await port.deleteWorkflowRequestAttachment(requestID, field.key, attachment.id);
      }
      if (!mounted.current) return;
      publishAttachments(latest.current.attachments.filter((item) => item.id !== attachment.id));
      setCheckedAttachments((current) => {
        const next = { ...current };
        delete next[attachment.id];
        return next;
      });
    } catch (reason) {
      if (mounted.current) setLocalError(reason instanceof Error ? reason.message : '파일을 삭제하지 못했습니다.');
    } finally {
      if (mounted.current) { busyRef.current = false; setBusy(false); setAnchor(undefined); setSelected(undefined); }
    }
  };

  const openAttachmentMenu = async (event: MouseEvent<HTMLElement>, attachment: WorkflowAttachment) => {
    setSelected(attachment);
    setAnchor(event.currentTarget);
    if (fixtureMode || busyRef.current || checkingRef.current) return;
    checkingRef.current = true;
    setChecking(true);
    setLocalError('');
    try {
      const requestID = await ensureDraft();
      if (!mounted.current) return;
      const refreshed = await port.getWorkflowRequestAttachment(requestID, field.key, attachment.id);
      if (!mounted.current) return;
      applyRefreshedAttachments([attachment], [refreshed]);
      setSelected((current) => current?.id === attachment.id ? latest.current.attachments.find((item) => item.id === attachment.id) : current);
    } catch (reason) {
      if (mounted.current && latest.current.attachments.some((item) => item.id === attachment.id)) {
        setLocalError(reason instanceof Error ? reason.message : '파일 상태를 확인하지 못했습니다.');
      }
    } finally {
      if (mounted.current) { checkingRef.current = false; setChecking(false); }
    }
  };

  const attachmentChips = attachments.map((attachment) => <WorkflowFileChip
    key={attachment.id}
    file={attachment}
    onClick={(event) => {
      event.stopPropagation();
      void openAttachmentMenu(event, attachment);
    }}
  />);

  return <Stack spacing={1}>
    <AttachmentField
      state={state}
      readOnly={disabled}
      disabled={remaining <= 0}
      busy={busy}
      busyLabel="파일을 업로드하고 있습니다."
      multiple
      accept={acceptedFileExtensions}
      actionLabel={remaining <= 0 ? `최대 ${maximum}개를 모두 등록했습니다.` : '파일을 끌어다 놓거나 선택하세요'}
      helperText={!disabled ? `PDF, 한글·Office, 이미지, 텍스트·데이터, EML, ZIP · ${attachments.length}/${maximum}개` : undefined}
      error={localError || undefined}
      testId={`workflow-file-dropzone-${field.key}`}
      inputTestId={`workflow-file-input-${field.key}`}
      onFilesSelected={(files) => { void uploadFiles(files); }}
    >
      {attachmentChips}
    </AttachmentField>
    {unresolvedAttachments.length > 0 ? <Stack spacing={0.5}>
      {unresolvedAttachments.map((attachment) => <Typography key={attachment.id} variant="caption" role="status" color={attachment.scanStatus === 'PENDING' ? 'text.secondary' : 'error'}>
        {attachment.displayName}: {attachment.scanStatus === 'PENDING' ? '보안 검사 중입니다. 검사가 끝난 뒤 제출할 수 있습니다.' : attachment.scanStatus === 'INFECTED' ? '보안 검사에서 차단되었습니다. 이 파일을 삭제하고 안전한 파일로 교체해 주세요.' : '보안 검사를 완료하지 못했습니다. 상태를 다시 확인하거나 파일을 교체해 주세요.'}
      </Typography>)}
      <Button size="small" disabled={checking || busy} onClick={() => void refreshScanStatuses()} sx={{ alignSelf: 'flex-start' }}>{checking ? '파일 상태 확인 중…' : '파일 상태 다시 확인'}</Button>
    </Stack> : null}
    <AnchoredMenu anchorEl={anchor ?? null} open={Boolean(anchor && selected)} onClose={() => { setAnchor(undefined); setSelected(undefined); }}>
      <MenuItem disabled={checking || selected?.scanStatus !== 'CLEAN'} onClick={() => selected && void download(selected, true)}>
        <VisibilityOutlined fontSize="small" sx={{ mr: 1 }} />파일 보기
      </MenuItem>
      <MenuItem disabled={checking || selected?.scanStatus !== 'CLEAN'} onClick={() => selected && void download(selected, false)}>
        <DownloadOutlined fontSize="small" sx={{ mr: 1 }} />파일 다운로드
      </MenuItem>
      <MenuItem disabled={disabled || busy} onClick={() => selected && void remove(selected)} sx={{ color: designTokens.color.danger }}>
        <DeleteOutlined fontSize="small" sx={{ mr: 1 }} />파일 삭제
      </MenuItem>
      {checking || selected?.scanStatus !== 'CLEAN' ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 260, px: 2, py: 1 }}>
        {checking ? <CircularProgress size={16} aria-label="파일 상태를 확인하는 중입니다." /> : selected?.scanStatus === 'PENDING' ? '보안 검사가 끝나면 파일을 열거나 다운로드할 수 있습니다.' : selected?.scanStatus === 'INFECTED' ? '보안 검사에서 차단된 파일입니다. 안전한 파일로 교체해 주세요.' : '보안 검사에 실패했습니다. 상태를 다시 확인하거나 파일을 교체해 주세요.'}
      </Typography> : null}
    </AnchoredMenu>
  </Stack>;
}

export type WorkflowReferenceFieldControlProps = {
  value: unknown;
  disabled: boolean;
  state: FieldControlState;
  port: Pick<WorkflowRequestPort, 'searchWorkflowRequestReferences'>;
  fixtureMode: boolean;
  fixtureOptions: readonly WorkflowRequest[];
  currentRequestID?: string;
  onChange: (value: WorkflowReferenceValue[]) => void;
};

/** 사용자가 이미 열람할 수 있는 제출 문서만 서버 검색 결과에서 선택합니다. */
export function WorkflowReferenceFieldControl({
  value,
  disabled,
  state,
  port,
  fixtureMode,
  fixtureOptions,
  currentRequestID,
  onChange,
}: WorkflowReferenceFieldControlProps) {
  const selected = useMemo(() => referencesFromValue(value), [value]);
  const selectedIDs = selected.map((item) => item.id).join(',');
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<WorkflowReferenceValue[]>(selected);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (disabled) return undefined;
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError('');
      const request = fixtureMode
        ? Promise.resolve({ data: fixtureOptions.map(referenceValue) })
        : port.searchWorkflowRequestReferences(query);
      void request.then((response) => {
        if (!active) return;
        const next = response.data
          .filter((item) => item.id !== currentRequestID && item.status !== 'DRAFT' && item.status !== 'CANCELLED');
        setOptions([
          ...selected,
          ...next.filter((item) => !selected.some((selectedItem) => selectedItem.id === item.id)),
        ]);
      }).catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : '참조 문서를 검색하지 못했습니다.');
      }).finally(() => {
        if (active) setLoading(false);
      });
    }, 250);
    return () => { active = false; window.clearTimeout(timer); };
  }, [port, currentRequestID, disabled, fixtureMode, fixtureOptions, query, selectedIDs]);

  return <FormAutocomplete
    multiple
    disableCloseOnSelect
    limitTags={3}
    id={state.controlId}
    options={options}
    value={selected}
    disabled={disabled}
    loading={loading}
    filterOptions={(items) => items}
    getOptionLabel={(option) => option.title}
    isOptionEqualToValue={(option, candidate) => option.id === candidate.id}
    noOptionsText={query.trim() ? '검색 결과가 없습니다.' : '참조할 수 있는 문서가 없습니다.'}
    loadingText="문서를 검색하는 중…"
    clearText="참조 문서 모두 해제"
    openText="문서 검색 열기"
    onInputChange={(_, next, reason) => { if (reason === 'input') setQuery(next); }}
    onChange={(_, next) => onChange(next)}
    renderOption={(props, option) => {
      const { key, ...optionProps } = props;
      return <Box component="li" key={key} {...optionProps}>
        <LinkOutlined fontSize="small" sx={{ mr: 1.25, color: designTokens.color.action }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 750 }}>{option.title}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {[option.templateName, option.requesterName, option.createdAt ? formatWorkflowDate(option.createdAt) : undefined].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      </Box>;
    }}
    input={{
      label: '참조 문서 검색',
      placeholder: '제목, 양식명 또는 요청자 검색',
      error: Boolean(error),
      helperText: error || undefined,
      ariaLabelledBy: state.labelId,
      ariaDescribedBy: error ? `${state.controlId}-helper-text` : state.messageId,
      startAdornment: selected.length > 0 ? <LinkOutlined fontSize="small" sx={{ mr: 0.5, color: designTokens.color.action }} /> : undefined,
    }}
  />;
}
